/**
 * @param InputText Base widget for extending
 * @returns Input Date Extended class
 */
function InputDateNumericClassCreator(InputText: import('widgets/forms/InputText').TInputText) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputTel
     * @augments InputTel
     * @classdesc Widget used as input that accepts date numbers only.
     */

    class InputDateNumeric extends InputText {
        prevValidValue = '';

        prefs() {
            return {
                prefillZero: false,
                ...super.prefs()
            };
        }

        /**
         * @description InputDateNumeric constructor
         * @augments InputText
         * @classdesc InputDateNumeric extends InputText widget, allows only digits and adds leading zero if needed
         */
        init() {
            super.init();

            this.addCurrentLeadingZero();
        }

        /**
         * @description Tries to change value of input. In case if new value is behind specific number input constrains - returns previous or default value.
         * @emits InputDateNumeric#attemptdecreaseminval
         * @listens dom#change
         */
        changeValue() {
            if (this.getValue() === '') {
                this.setValue(String(this.prevValidValue));
            }

            this.setValue(this.getValue());
            this.emit('inputchanged');
        }

        /**
         * @description Adds leading zero to the value if needed. If value is more than 1 digit, removes leading zeros.
         * @param rawValue input value
         * @returns value with leading zero if needed
         */
        addLeadingZero(rawValue: string): string {
            let value = rawValue;

            if (this.prefs().prefillZero && value && value.length === 1 && !value.startsWith('0')) {
                value = '0' + value;
            }

            if ((rawValue && rawValue.length > 1 && !value.startsWith('0')) || rawValue.length > 2) {
                // Remove leading zeros if the value has more than one digit
                value = value.replace(/^0+/, '');
            }

            return value;
        }

        /**
         * @description Get current Digits Only value of input
         * @returns current Digits Only value of input
         */
        getCurrentDigitsOnlyValue(): string {
            const currentValue = this.getValue();

            return currentValue.replace(/\D/g, '');
        }

        /**
         * @description Adds leading zero to the current value if needed. If value is more than 1 digit, removes leading zeros.
         * This method is used to ensure that the input value always has a leading zero if it is a single digit.
         */
        addCurrentLeadingZero() {
            const digitsOnlyValue = this.getCurrentDigitsOnlyValue();

            super.setValue(this.addLeadingZero(digitsOnlyValue.toString()), true); // allow user to insert any values
        }

        /**
         * @description Handle input event. On this event we allow user to insert any values, but filter only digits
         * and emit "inputchanged" event to parent widget. Also we filter values to store any valid value as prevValidValue
         * so on blur we could insert one of it.
         * @listens dom#input
         * @emits InputNumber#inputchanged
         */
        handleInput(): void {
            const digitsOnlyValue = this.getCurrentDigitsOnlyValue();

            this.addCurrentLeadingZero();

            this.prevValidValue = this.filterValue(digitsOnlyValue);
            this.emit('inputchanged');
        }

        /**
         * @description Handle blur event. On this event we add leading zero if needed and validate input value.
         * Also if input value is invalid, we set previous valid value or empty string if there is no valid value.
         * @listens dom#blur
         */
        handleBlur(): void {
            const inputValue = this.getCurrentDigitsOnlyValue().toString();

            if (inputValue?.length === 1 && !inputValue.startsWith('0')) {
                this.setValue('0' + inputValue, true);
            }

            this.validate();
        }

        /**
         * @description Get valid input values from raw input
         * @param rawValue input value
         * @returns valid value string
         */
        filterValue(rawValue: string): string {
            return this.addLeadingZero(rawValue);
        }

        /**
         * @description Highlights input field with error state
         * @param isError if true, highlights input field with error state, otherwise highlights with valid state
         */
        toggleErrorState(isError?: boolean): void {
            const field = this.ref('field');
            const self = this.ref('self');

            field.toggleClass(this.prefs().classesErrorState, isError);
            field.toggleClass(this.prefs().classesValidState, !isError);
            field.attr('aria-invalid', isError ? 'true' : 'false');
            self.toggleClass(this.prefs().classesWrapperErrorState, isError);
            self.toggleClass(this.prefs().classesWrapperValidState, !isError);
        }

        /**
         * @description Calls parent setError method and emits togglegeneralerrorstate event
         * @param [error] error message - if defined, will set error state, and valid state - otherwise
         * @emits InputDateNumeric#togglegeneralerrorstate
         */
        setError(error?: string): void {
            super.setError(error);

            /**
             * @description Highlights the date numeric fields and set the general error message
             * @event InputDateNumeric#toggleGeneralErrorState
             */
            if (error) {
                this.emit('togglegeneralerrorstate');
            }
        }
    }

    return InputDateNumeric;
}

export type TInputDateNumeric = ReturnType<typeof InputDateNumericClassCreator>;

export type TInputDateNumericInstance = InstanceType<TInputDateNumeric>;

export default InputDateNumericClassCreator;
